import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';

import { formatFileSize } from '../../utils/formatFileSize';
import { Divider } from '../Divider/Divider';
import { Filename } from '../Filename/Filename';
import { IconButton } from '../IconButton/IconButton';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';

interface Props {
  /** List of added files */
  files: File[];
  /** Remove file callback */
  onRemoveFile: (removedFile: File, removedFileIndex?: number) => void;
  /** List title */
  title: string;
}

export const Files = memo(({ files, onRemoveFile, title }: Props) => {
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  if (files.length === 0) return null;
  return (
    <Layout preset="subsections">
      <Divider label={`${title} (${formatNumber(files.length)})`} />
      <Layout direction="column" role="list">
        {files.map((file, index) => {
          return (
            <Layout key={index} justify="space-between" role="listitem">
              <Filename name={file.name} includeExtension={true} />
              <Layout align="center" preset="xs">
                <Text>{formatFileSize(file.size, intl)}</Text>
                <IconButton
                  icon="x"
                  onClick={() => onRemoveFile(file, index)}
                  tooltip={intl.formatMessage(
                    {
                      id: 'Files.removeFile',
                      defaultMessage: 'Remove {fileName}',
                      description:
                        'Tooltip for removing a file from the list. Example: fileName = "document.pdf"',
                    },
                    {
                      fileName: file.name,
                    },
                  )}
                />
              </Layout>
            </Layout>
          );
        })}
      </Layout>
    </Layout>
  );
});
